<template>
  <div>
    <PrimeDataTable
      ref="dataTable"
      :tableConfig="tableConfig"
      :tableOptions="tableOptions"
      :page-size="pageSize"
      :loadDataFunction="loadUserInvitations"
      @selectedColumns="tableToggleData"
      :paginator="true"
    />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
// import PermissionLevel from "@/assets/js/enum/PermissionLevel";
import { mapGetters, mapState, mapActions } from 'vuex';
import { xhr } from '@/xhr/endpoints';

export default {
  name: 'UserInvitations',
  components: {
    PrimeDataTable: defineAsyncComponent(() => import('@/components/table/PrimeDataTable.vue')),
  },
  props: {
    loadDataFunction: Function,
  },
  data() {
    return {
      pageSize: 5,
      userOrganizationNames: [],
      orgOptionsLoaded: false,
      debateHostNames: [],
      inviteeOptions: [],
    };
  },
  computed: {
    ...mapGetters(['tableToggleData']),
    ...mapState(['idToken', 'userData']),
    tableConfig() {
      return [
        {
          column: 'Debate Title',
          field: 'invitation.document.title',
          model: row => row.invitation?.document?.title || 'debate title',
          link: row => `/invitation/${row.invitation._id}`,
          sortable: true,
          tooltip: 'Click to see Debate Insights',
          // filter: 'text',
          ellipsis: true,
          index: 1,
        },
        {
          column: 'Author',
          field: 'invitation.document.author.name', // Filler for column rename TODO: Fix
          sortable: true,
          model: row => row.invitation?.document?.author?.name,
          index: 2,
        },
        {
          column: 'Publisher',
          field: 'invitation.document.publication', // Filler for column rename TODO: Fix
          sortable: true,
          model: row => row.invitation?.document?.publication,
          ellipsis: true,
          index: 3,
        },
        {
          column: 'Debate Host',
          field: 'invitation.invitedBy.full_name',
          model: row =>
            row.invitation?.invitedBy?._id === this.userData.id
              ? 'Me'
              : row.invitation?.document?.organization
                ? this.userOrganizationNames.filter(
                    organization => organization.value === row.invitation?.document?.organization
                  )[0].name
                : row.invitation?.invitedBy?.firstname +
                    ' ' +
                    row.invitation?.invitedBy?.lastname || '',
          sortable: true,
          // filter: {
          //   type: 'select',
          //   optionLabel: 'name',
          //   options: [...this.debateHostNames, ...this.userOrganizationNames],
          // },
          index: 4,
        },
        {
          column: 'Affiliation',
          field: 'invitation.invitedBy.affiliation',
          model: row =>
            row.invitation?.invitedBy?.affiliation ? row.invitation?.invitedBy?.affiliation : '-',
          index: 5,
        },
        {
          column: 'Invitee',
          field: 'invitee',
          sortable: true,
          model: row =>
            row.invitation?.invitedBy?._id !== this.userData.id
              ? 'Me'
              : (row.invitation?.invitee?.firstname || '') +
                ' ' +
                (row.invitation?.invitee?.lastname || ''),
          // filter: {
          //   type: 'select',
          //   options: this.inviteeOptions
          // },
          index: 6,
        },
        {
          column: 'Role',
          field: 'invitation.invitee.role',
          model: row => row.invitation?.invitee?.role || '',
          sortable: true,
          // filter: {
          //   type: 'select',
          //   options: [PermissionLevel.TEAM_LEAD, PermissionLevel.COHORT],
          // },
          index: 7,
        },

        {
          column: 'Invitee Status',
          field: 'status',
          sortable: true,
          model: row => row.status,
          // filter: {
          //   type: 'select',
          //   options: ['Accepted', 'Declined', 'Expired', 'Invited']
          // },
          index: 8,
          border: true,
        },
        {
          column: 'Date Received',
          field: 'createdAt',
          model: row =>
            new Date(row.invitation.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              timeZone: 'America/New_York',
            }),
          sortable: true,
          index: 9,
        },
        {
          column: '',
          field: 'archived',
          filter: {
            type: 'checkbox',
            text: 'Show Archived Invitations',
          },
        },
      ];
    },
    tableOptions() {
      return {
        // globalFilterFields: ['invitation.document.title','invitation.document.author.name','invitation.invitedBy.full_name', 'invitee', 'invitation.document.publication','invitation.invitedBy.affiliation'],
        tableName: 'tableUserInvitations',
        archiveSelected: {
          action: async ({ items, success, failed }) => {
            await this.archiveMultipleDocuments(items, success, failed, true);
          },
          title: 'Archive Documents',
          tooltip: 'Archive the checked items',
        },
        restoreSelected: {
          action: async ({ items, success, failed }) => {
            await this.archiveMultipleDocuments(items, success, failed, false);
          },
          title: 'Restore Documents',
          tooltip: 'Restore archived items',
        },
      };
    },
  },
  async mounted() {
    (await xhr.getUserOrganizationNames(this.idToken)).map(x => {
      if (x && x.name) {
        this.userOrganizationNames.push({
          name: x?.name,
          value: x?._id,
        });
      }
    });
    this.orgOptionsLoaded = true;
  },

  methods: {
    ...mapActions(['updateUserInvitation']),
    async loadUserInvitations({ page, resultsPerPage, sortColumn, sortDirection, filters }) {
      try {
        let res = await this.loadDataFunction({
          page,
          resultsPerPage,
          sortColumn,
          sortDirection,
          filters,
        });
        this.debateHostNames = [];
        this.inviteeOptions = [];
        res.results.map(item => {
          if (
            !this.debateHostNames.filter(data => data.value === item.invitation?.invitedBy?._id)
              .length
          ) {
            this.debateHostNames.push({
              name:
                this.userData.id === item.invitation?.invitedBy?._id
                  ? 'Me'
                  : item.invitation?.invitedBy?.firstname +
                    ' ' +
                    item.invitation?.invitedBy?.lastname,
              value: item.invitation?.invitedBy?._id,
            });
          }
        });
        res.results.map(item => {
          if (
            !this.inviteeOptions.includes(
              (item.invitation?.invitee?.firstname || '') +
                ' ' +
                (item.invitation?.invitee?.lastname || '')
            )
          ) {
            if (item.invitation?.invitedBy?._id !== this.userData.id) {
              if (!this.inviteeOptions.includes('Me')) this.inviteeOptions.push('Me');
            } else
              this.inviteeOptions.push(
                (item.invitation?.invitee?.firstname || '') +
                  ' ' +
                  (item.invitation?.invitee?.lastname || '')
              );
          }
        });
        return res;
      } catch (err) {
        console.error(err);
      }
    },

    async archiveMultipleDocuments(items, success, failed, archive) {
      try {
        for (let i = 0; i < items.length; i++) {
          await this.updateUserInvitation(
            { invitationId: items[i].invitation._id, archive },
            this.idToken
          );
        }
        if (success) success();
        this.$refs.dataTable.loadData();
      } catch (e) {
        console.log(e);
        if (failed) failed();
      }
    },
  },
};
</script>
